import { useEffect, useState } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import { Send, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

const Badge = ({ kind, value }) => {
  const styles = {
    status: {
      UNPAID: "bg-[#FEF3C7] text-[#B45309]",
      PARTIAL: "bg-[#DBEAFE] text-[#1D4ED8]",
      PAID: "bg-[#D1FAE5] text-[#059669]",
      DELETED: "bg-[#FEE2E2] text-[#DC2626]",
    },
    wa: {
      PENDING: "bg-[#F1F5F9] text-[#475569]",
      SENT: "bg-[#D1FAE5] text-[#059669]",
      FAILED: "bg-[#FEE2E2] text-[#DC2626]",
    },
  };
  const map = styles[kind] || {};
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[value] || "bg-[#F1F5F9] text-[#475569]"}`}>
      {value}
    </span>
  );
};

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [sendingId, setSendingId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const params = filterStatus ? { status: filterStatus } : {};
      const { data } = await api.get("/invoices", { params });
      if (alive) {
        setItems(data);
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [filterStatus, reloadKey]);

  const handleSend = async (inv) => {
    setSendingId(inv.accurate_id);
    try {
      const { data } = await api.post(`/invoices/${inv.accurate_id}/send`);
      if (data.ok) {
        toast.success(`Invoice ${inv.number} terkirim ke ${inv.customer_name}`);
      } else {
        toast.error(`Gagal kirim: ${data.error || "Unknown error"}`);
      }
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setSendingId(null);
    }
  };

  const [urlFilter, setUrlFilter] = useState(new URLSearchParams(window.location.search).get("filter") || "");

  const filtered = items.filter((i) => {
    let match = true;
    if (urlFilter) {
      if (i.status === "PAID") {
        match = false;
      } else {
        const today = new Date();
        today.setHours(0,0,0,0);
        const due = new Date(i.due_date);
        due.setHours(0,0,0,0);
        const diffTime = due - today;
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        
        if (urlFilter === "h3" && diffDays !== 3) match = false;
        if (urlFilter === "h7" && diffDays !== 7) match = false;
        if (urlFilter === "h14" && diffDays !== 14) match = false;
        if (urlFilter === "h15" && diffDays !== 15) match = false;
        if (urlFilter === "h30" && diffDays !== 30) match = false;
        if (urlFilter === "h45" && diffDays !== 45) match = false;
        if (urlFilter === "overdue" && diffDays >= 0) match = false;
      }
    }
    if (match && search) {
      const s = search.toLowerCase();
      match = i.number.toLowerCase().includes(s) || i.customer_name.toLowerCase().includes(s);
    }
    return match;
  });

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="invoices-page">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-[#0F172A]">Invoices</h1>
        <p className="text-sm text-[#64748B] mt-1">
          Invoice dari Accurate.id. Kirim ke WhatsApp pelanggan dengan satu klik.
        </p>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nomor invoice atau pelanggan..."
            data-testid="invoice-search-input"
            className="w-full bg-white border border-[#E2E8F0] rounded-md pl-9 pr-3 py-2 text-sm focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] outline-none"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="invoice-status-filter"
          className="bg-white border border-[#E2E8F0] rounded-md px-3 py-2 text-sm focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] outline-none"
        >
          <option value="">Semua Status</option>
          <option value="UNPAID">Unpaid</option>
          <option value="PARTIAL">Partial</option>
          <option value="PAID">Paid</option>
          <option value="DELETED">Deleted (Accurate)</option>
        </select>
      </div>

      <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FA]">
            <tr className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
              <th className="px-6 py-3 text-left">Invoice</th>
              <th className="px-6 py-3 text-left">Pelanggan</th>
              <th className="px-6 py-3 text-left">Jatuh Tempo</th>
              <th className="px-6 py-3 text-right">Jumlah</th>
              <th className="px-6 py-3 text-right">Uang Masuk</th>
              <th className="px-6 py-3 text-right">Sisa Tagihan</th>
              <th className="px-6 py-3 text-center">Status</th>
              <th className="px-6 py-3 text-center">WhatsApp</th>
              <th className="px-6 py-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {loading && (
              <tr>
                <td colSpan="9" className="px-6 py-12 text-center text-sm text-[#64748B]">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Memuat...
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan="9" className="px-6 py-12 text-center text-sm text-[#64748B]">
                  Tidak ada invoice.
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((inv) => (
                <tr key={inv.accurate_id} className="hover:bg-[#F8F9FA]" data-testid={`invoice-row-${inv.accurate_id}`}>
                  <td className="px-6 py-3 font-mono text-xs text-[#0F172A]">{inv.number}</td>
                  <td className="px-6 py-3 text-[#0F172A]">{inv.customer_name}</td>
                  <td className="px-6 py-3 text-[#475569]">{formatDate(inv.due_date)}</td>
                  <td className="px-6 py-3 text-right tabular-nums text-[#0F172A] font-medium">
                    {formatIDR(inv.amount)}
                  </td>
                  <td className="px-6 py-3 text-right tabular-nums text-[#059669] font-medium">
                    {formatIDR(inv.amount - (inv.outstanding_amount || 0))}
                  </td>
                  <td className="px-6 py-3 text-right tabular-nums text-[#DC2626] font-medium">
                    {formatIDR(inv.outstanding_amount || 0)}
                  </td>
                  <td className="px-6 py-3 text-center">
                    <Badge kind="status" value={inv.status} />
                  </td>
                  <td className="px-6 py-3 text-center">
                    <Badge kind="wa" value={inv.wa_status} />
                  </td>
                  <td className="px-6 py-3 text-right">
                    <button
                      onClick={() => handleSend(inv)}
                      disabled={sendingId === inv.accurate_id || inv.status === "PAID" || inv.status === "DELETED"}
                      data-testid={`send-invoice-button-${inv.accurate_id}`}
                      className="inline-flex items-center gap-1.5 bg-white border border-[#E2E8F0] hover:border-[#0066FF] hover:text-[#0066FF] text-[#0F172A] rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sendingId === inv.accurate_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Send className="w-3.5 h-3.5" />
                      )}
                      Kirim WA
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
